import React, { useCallback, useEffect, useState } from "react";
import useApiFetcher from "../../hooks/useApiFetcher";
 
import { CiSearch } from "react-icons/ci";
import { debounce } from "../Avatar/Utility";
import { MdEdit } from "react-icons/md";
import { MdDelete } from "react-icons/md";
import LoaderSpiner from "../LoaderSpiner";
import { useTheme } from "../BrandColors/Context/ThemeContext";

const columns = [
  { _id: 1, columnName: "No" },
  { _id: 2, columnName: "Product Name" },
  { _id: 3, columnName: "Description" },
  { _id: 4, columnName: "Price" },
  { _id: 5, columnName: "Action" }
];

const AddProduct = () => {
  const { apiFetcher } = useApiFetcher();
  const theme = useTheme() || "#3b82f6";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [Products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [isDebounceCompleted, setisDebounceCompleted] = useState(false);
  const [filteredProducts, setfilteredProducts] = useState([]);
  const [query, setquery] = useState("");
  const [editing, setIsediting] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  const fetchProductsByname = (query) => {
    if (!query) {
      setfilteredProducts([]);
      setisDebounceCompleted(false);
      return;
    }
    const filteredNames = Products?.filter((product) => {
      return product.name.toLowerCase().includes(query.toLowerCase());
    });
    setfilteredProducts(filteredNames);
    setisDebounceCompleted(true);
  };


  const fetchproducts = async () => {
    try {
      setLoading(true);
      const response = await apiFetcher("/products");
      setProducts(response);
      setLoading(false);
    } catch (error) {
      console.error(error.errorMessage);
      setError("An error occurred while fetching products");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchproducts()
  }, [])

  const clearInputfields = () => {
    setName("")
    setDescription("")
    setPrice("")
  }

  const handleCencelEdit = () => {
    setIsediting(false)
    clearInputfields()
  }

  const handleAddOrUpdateProduct = async (e) => {
    e.preventDefault();

    const payload = {
      name,
      description,
      price,
    };

    const method = editing ? "PUT" : "POST"
    const url = editing ? `/products/${editingUserId}` : `/products/`
    try {
      await apiFetcher(url, method, payload);
      fetchproducts()
      setIsediting(false)
      setEditingUserId(null)
      clearInputfields()
    } catch (error) {
      console.error("error failed:", error.message);
    }
  };
  
  const handleDelete = async (product) => {
    try {
      await apiFetcher(
        `/products/${product._id}`,
        "DELETE"
      );
      fetchproducts();
      if(editingUserId === product._id) {
        setIsediting(false)
        setEditingUserId(null)
        clearInputfields()
      }
    } catch (error) {
      console.log(error.message, "some error occured");
    }
  }

  const handleEdit = (product) => {
    setIsediting(true)
    setEditingUserId(product._id)
    setName(product?.name)
    setDescription(product.description)
    setPrice(product.price)
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const debouncedfn = useCallback(debounce(fetchProductsByname, 300), [Products]);

  useEffect(() => {
    debouncedfn(query);
  }, [query]);

  const [currentPage, setCurrentPage] = useState(1);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Calculate sequential ID based on page number and index
  const itemsPerPage = 5;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts?.length > 0 ? filteredProducts?.slice(indexOfFirstItem, indexOfLastItem) : Products?.slice(indexOfFirstItem, indexOfLastItem);

  // Total pages calculation
  const totalPages = Math.ceil((filteredProducts?.length > 0 ? filteredProducts?.length : Products?.length) / itemsPerPage) || 1;

  const renderTable = () => {
    const itemsToRender = filteredProducts.length > 0 ? filteredProducts : currentItems;
    
    return itemsToRender.length > 0 && itemsToRender.map((product, index) => {
        const sequentialId = (currentPage - 1) * itemsPerPage + (index + 1);
        
        return (
          <tr key={product._id} className="text-slate-700 text-sm hover:bg-white/5 transition-colors group border-b border-blue-200/50">
            <td className="px-4 py-4">{sequentialId}</td>
            <td className="px-4 py-4 font-medium text-white">{product.name}</td>
            <td className="px-4 py-4 truncate max-w-[200px]" title={product.description}>{product.description}</td>
            <td className="px-4 py-4 text-green-400 font-medium">{`$${product.price}`}</td>

            <td className="px-4 py-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleEdit(product)}
                  className="text-primary-400 hover:text-primary-300 hover:scale-110 transition-transform p-1.5 rounded-full hover:bg-primary-900/30"
                  title="Edit"
                >
                  <MdEdit size={18} />
                </button>
                <button
                  onClick={() => handleDelete(product)}
                  className="text-red-400 hover:text-red-300 hover:scale-110 transition-transform p-1.5 rounded-full hover:bg-red-900/30"
                  title="Delete"
                >
                  <MdDelete size={18} />
                </button>
              </div>
            </td>
          </tr>
        );
    });
  };

  return (
    <div className="glass-panel w-full flex flex-col p-6 animate-fade-in-up mt-8 min-h-[60vh] max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-white">Product Management</h2>
      </div>

      <form onSubmit={handleAddOrUpdateProduct} className="bg-white/50 border border-blue-200/50 rounded-xl p-6 mb-8 shadow-lg">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 border-b border-blue-200/50 pb-2">
            {editing ? "Edit Product Details" : "Add New Product"}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="name" className="text-sm font-medium text-slate-700">
              Product Name
            </label>
            <input
              type="text"
              placeholder="e.g. Extended Warranty"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="input-field"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="price" className="text-sm font-medium text-slate-700">
              Price ($)
            </label>
            <input
              type="number"
              id="price"
              placeholder="0.00"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
              min="0"
              step="0.01"
              className="input-field"
            />
          </div>
        </div>
        
        <div className="flex flex-col gap-1.5">
          <label htmlFor="description" className="text-sm font-medium text-slate-700">
            Description
          </label>
          <textarea
            id="description"
            placeholder="Detailed product description..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            className="input-field min-h-[100px] resize-y"
          />
        </div>
        
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-blue-200/50">
         {editing && (
            <button 
              type="button"
              onClick={handleCencelEdit} 
              className="px-5 py-2.5 rounded-lg font-medium text-slate-700 bg-white hover:bg-blue-100 transition-colors border border-blue-200"
            >
              Cancel
            </button>
          )}
          <button 
            type="submit" 
            className="px-5 py-2.5 rounded-lg font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0"
            style={{ backgroundColor: theme }}
          >
            {editing ? "Update Product" : "Add Product"}
          </button>
        </div>
      </form>

      {/* Product List */}
      <div className="flex-1 flex flex-col">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Product Directory</h3>
          
          <div className="relative w-full sm:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-600">
              <CiSearch size={20} />
            </div>
            <input
              type="text"
              value={query}
              onChange={(event) => setquery(event.target.value)}
              className="input-field pl-10 w-full py-2"
              placeholder="Search products..."
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoaderSpiner />
          </div>
        ) : error ? (
          <div className="text-red-400 font-medium bg-red-900/20 p-4 rounded-lg text-center">{error}</div>
        ) : (
          <>
            <div className="w-full overflow-x-auto rounded-lg border border-blue-200/50 scrollbar-hide">
              <table className="w-full text-left border-collapse min-w-max">
                <thead>
                  <tr className="text-white text-sm font-semibold whitespace-nowrap" style={{ backgroundColor: theme }}>
                    {columns.map(({ _id, columnName }, index) => {
                       let roundedClass = "";
                       if(index === 0) roundedClass = "rounded-tl-lg";
                       if(index === columns.length - 1) roundedClass = "rounded-tr-lg";
                       return <th key={_id} className={`px-4 py-4 ${roundedClass}`}>{columnName}</th>;
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50 bg-white/30">
                  {isDebounceCompleted && filteredProducts.length === 0 && query !== "" ? (
                    <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-slate-500">No products found matching "{query}"</td></tr>
                  ) : filteredProducts.length === 0 && currentItems.length === 0 ? (
                    <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-slate-500">No products available.</td></tr>
                  ) : (
                    renderTable()
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="flex justify-center items-center mt-6 gap-2 pt-4">
                <button
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white text-slate-700 hover:bg-blue-100"
                >
                  Prev
                </button>
                
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i + 1)}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center font-medium text-sm transition-all ${
                        currentPage === i + 1 
                          ? 'text-white shadow-lg scale-105'
                          : 'bg-white text-slate-600 hover:bg-blue-100 hover:text-white'
                      }`}
                      style={currentPage === i + 1 ? { backgroundColor: theme } : {}}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white text-slate-700 hover:bg-blue-100"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddProduct;
